import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { Character } from '../Character';
import { WeaponType } from '../../weapons/weapon-type';
import { BodyPart } from '../../enums/BodyPart';
import { Weapon } from '../../weapons/Weapon';
import { CharacterSocket } from './CharacterSocket';

/**
 * Manages weapon attachment and interaction for a character.
 *
 * @deprecated
 * Weapon rigging workflow:
 * - CharacterSocket.LeftHand and CharacterSocket.RightHand are empties set in Blender on the character model hand position.
 * - WeaponBone.left and right weapon grips are defined in Blender on the weapon model.
 * - At runtime, in the loadWeapon() method, the right grip bone is attached to the right hand socket.
 * - IK is applied so the left grip bone follows the left hand socket.
 * @tmp Replacement system (temporary):
 * The weapon is now merged directly into the character armature in Blender and is always present
 * in the scene graph. It carries custom properties { data: "weapon", type: "pistol"|"rifle" }.
 * On character load, all weapon objects are hidden (visible = false).
 * To equip a weapon, call showWeaponOnModel(type) — it hides all weapons then reveals the matching one.
 * To unequip, call hideAllWeaponsOnModel() — hides every weapon and resets hasWeaponLoaded.
 * No external GLTF load is needed; the weapon transform is fully controlled in Blender.
 */
export class WeaponInteractionManager {
    private readonly character: Character;
    private weapon: Weapon;

    constructor(character: Character) {
        this.character = character;
    }

    private getSocket(socketName: CharacterSocket): THREE.Object3D | undefined {
        let socket: THREE.Object3D | undefined;
        this.character.children.forEach(child => {
            const found = child.getObjectByName(socketName);
            if (found) { socket = found; }
        });
        return socket;
    }

    private findWeaponObject(weaponType: WeaponType): THREE.Object3D | undefined {
        let found: THREE.Object3D | undefined;
        this.character.traverse((child) => {
            if (child.userData?.data === 'weapon' && child.userData?.type === weaponType) {
                found = child;
            }
        });
        return found;
    }

    public showWeaponOnModel(weaponType: WeaponType): void {
        this.hideAllWeaponsOnModel();
        const weaponObject = this.findWeaponObject(weaponType);
        if (weaponObject) {
            weaponObject.visible = true;
        }
        this.character.hasWeaponLoaded = true;
    }

    public hideWeaponOnModel(weaponType: WeaponType): void {
        const weaponObject = this.findWeaponObject(weaponType);
        if (weaponObject) {
            weaponObject.visible = false;
        }
        this.character.hasWeaponLoaded = false;
    }

    public hideAllWeaponsOnModel(): void {
        this.character.traverse((child) => {
            if (child.userData?.data === 'weapon') {
                child.visible = false;
            }
        });
        this.character.hasWeaponLoaded = false;
    }

    /** @deprecated Use showWeaponOnModel instead. The weapon is now merged into the character armature in Blender. */
    public loadWeapon(weaponType: WeaponType): void {
        const loader = new GLTFLoader();
        console.log('Loading weapon:', weaponType);
        loader.load(`../../assets/weapons/${weaponType}.glb`, gltf => {
            this.weapon = new Weapon(gltf, weaponType);

            const rightHandSocket = this.getSocket(CharacterSocket.RightHand);
            if (rightHandSocket) {
                this.weapon.attachTo(rightHandSocket);
            }
        }, undefined, error => {
            console.error(error);
        });

        this.character.hasWeaponLoaded = true;
    }

    /** @deprecated Use hideWeaponOnModel or hideAllWeaponsOnModel instead. */
    public unloadWeapon(): void {
        const rightHandSocket = this.getSocket(CharacterSocket.RightHand);
        if (rightHandSocket) {
            const currentWeapon = rightHandSocket.children.find(child => child.type === this.weapon.identifier);
            if (currentWeapon) {
                rightHandSocket.remove(currentWeapon);
                this.character.hasWeaponLoaded = false;
            }
        }
    }

    lockAiming(character: Character): void {
        this.character.world.cameraOperator.aimingMode = true;
        this.character.world.cameraOperator.targetedCharacter = character;
    }

    unlockAiming(): void {
        this.character.world.cameraOperator.aimingMode = false;
    }

    public setRightHand(): void {
        this.character.children.forEach(children => {
            this.character.rightHand = children.getObjectByName(BodyPart.RightHand);
        });
    }
}
